
import React from 'react';
import { Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import PlaceSearch from '@/components/PlaceSearch';
import { PlaceResult } from '@/types/place';
import { Lead } from '@/types/lead';
import { SavedSearch } from '@/services/savedSearchService';
import LoadMoreButton from './LoadMoreButton';

interface SearchResultsProps {
  results: PlaceResult[];
  isSearching: boolean;
  searchQuery: string;
  locationQuery: string;
  radius: number;
  hasSearched: boolean;
  searchMeta: {
    query?: string;
    location?: string;
    radius?: number;
    geocoded?: boolean;
    endpoint?: string;
    useRadius?: boolean;
    searchLocation?: {
      lat: number;
      lng: number;
    };
  } | null;
  activeSavedSearch: SavedSearch | null;
  onImport: (place: PlaceResult) => Promise<Lead | null>;
  nextPageToken?: string;
  isLoadingMore: boolean;
  onLoadMore: () => Promise<void>;
  onRetry?: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isSearching,
  searchQuery,
  locationQuery,
  radius,
  hasSearched,
  searchMeta,
  activeSavedSearch,
  onImport,
  nextPageToken,
  isLoadingMore,
  onLoadMore,
  onRetry
}) => {
  if (!hasSearched) {
    return null;
  }

  const formatRadius = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Search Results</h2>
        {results.length > 0 && (
          <p className="text-sm text-muted-foreground">{results.length} businesses found</p>
        )}
      </div>
      
      {searchMeta && locationQuery && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Search Information</AlertTitle>
          <AlertDescription>
            {searchMeta.useRadius 
              ? (searchMeta.geocoded 
                  ? `Successfully searched for "${searchQuery || 'businesses'}" near "${locationQuery}" within a ${formatRadius(radius)} radius. Using ${searchMeta.endpoint === 'nearbysearch' ? 'strict radius filter' : 'approximate location matching'}.`
                  : `Location "${locationQuery}" couldn't be precisely located. Results may not be specific to that area.`)
              : `Searching for "${searchQuery || 'businesses'}" in "${locationQuery}" without radius restriction. This provides broader results across the entire area.`
            }
          </AlertDescription>
        </Alert>
      )}
      
      {activeSavedSearch && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Saved Search Loaded</AlertTitle>
          <AlertDescription>
            You're viewing results from your saved search "{activeSavedSearch.name}".
          </AlertDescription>
        </Alert>
      )}
      
      <PlaceSearch 
        results={results} 
        isLoading={isSearching} 
        onImport={onImport}
        searchQuery={searchQuery}
        onRetry={onRetry}
        searchLocation={searchMeta?.searchLocation}
        useRadius={searchMeta?.useRadius}
      />
      
      {nextPageToken && (
        <LoadMoreButton 
          isLoading={isLoadingMore} 
          onLoadMore={onLoadMore} 
        />
      )}
    </div>
  );
};

export default SearchResults;
